import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { jobCategoriesAPI } from '../services/api';
import { CategorySummary } from '../types';
import JobCard from '../components/JobCard';
import { EmptyState, SectionHeader } from '../components/ui';
import { SEO } from '../components/seo/SEO';

const CategoryParentLanding: React.FC = () => {
  const { parent: parentSlug } = useParams<{ parent: string }>();
  const [summary, setSummary] = React.useState<CategorySummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    if (!parentSlug) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    jobCategoriesAPI
      .getSummary(parentSlug)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [parentSlug]);

  if (loading) return <p>Loading…</p>;
  if (notFound || !summary) return <EmptyState title="Category not found" description="Browse all open jobs on the /jobs page." />;

  const cat = summary.category;
  const children = summary.children || [];
  const sampleJobs = summary.sample_jobs || [];
  const totalJobs = children.reduce((sum, c) => sum + c.job_count, 0);
  const title = `${cat.name} Jobs in India`;
  const description = `${totalJobs} open ${cat.name.toLowerCase()} role${totalJobs === 1 ? '' : 's'} across India. AI-ranked from top companies on talixjobs.`;

  return (
    <div className="page-container">
      <SEO title={title} description={description} />
      <nav className="crumbs">
        <Link to="/">Home</Link> ›
        <Link to="/jobs">Jobs</Link> ›
        <span>{cat.name}</span>
      </nav>

      <SectionHeader title={title} description={cat.description || undefined} />

      <h2>Browse by role</h2>
      {children.length === 0 ? (
        <EmptyState title="No sub-categories" description="Check back later." />
      ) : (
        <ul className="listing">
          {children.map((c) => (
            <li key={c.id}>
              <Link to={`/jobs/category/${cat.slug}/${c.slug}`}>
                <strong>{c.name}</strong>
              </Link>
              <div className="sub">
                {c.job_count} open role{c.job_count === 1 ? '' : 's'}
                {c.description ? ` · ${c.description}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      {sampleJobs.length > 0 && (
        <>
          <h2>Recent openings across {cat.name}</h2>
          {sampleJobs.map((j) => (
            <JobCard key={j.id} job={j} />
          ))}
          <p className="meta">
            <Link to={`/jobs?category=${cat.slug}`}>See all {cat.name.toLowerCase()} jobs →</Link>
          </p>
        </>
      )}
    </div>
  );
};

export default CategoryParentLanding;
